import React, { useState } from "react";
import { ContactsListPage } from "../../../../services/contactImportService";

type ContactsListCardPage = ContactsListPage & { query?: string };

interface ContactsListCardProps {
  page: ContactsListCardPage;
  onLoadMore: () => Promise<void>;
  onSearch: (query: string) => Promise<void>;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

// Inline color-mix() instead of Tailwind's bg-theme-*/NN opacity-suffix
// classes: theme.primary/secondary in tailwind.config.js resolve straight to
// `var(--color-primary)` etc, not the rgb(var(...) / <alpha-value>) shape
// Tailwind's opacity modifiers need — so bg-theme-primary/10 silently renders
// with NO background at all (confirmed live: the tag chips below rendered as
// bare text, no pill). Same fix ContactReviewCard.tsx already uses.
const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

const headerIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
};

const tagPillStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
  color: "var(--color-primary)",
};

/** Read-only "ask my contacts" listing (SGL-81) — a live GHL browse, not an
 *  import batch: no checkboxes, edit, drop, or tag/import actions. */
export const ContactsListCard: React.FC<ContactsListCardProps> = ({ page, onLoadMore, onSearch }) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInput, setSearchInput] = useState(page.query ?? "");

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const runSearch = async (value: string) => {
    setIsSearching(true);
    try {
      await onSearch(value);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between border-b border-theme-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={headerIconStyle}>
            <span className="material-icons-outlined text-[18px] text-theme-primary">group</span>
          </span>
          <p className="text-[13px] font-bold text-theme-fg">
            {page.total} {page.total === 1 ? "Contact" : "Contacts"} in GHL
          </p>
        </div>
        <span className="text-[11px] text-theme-fg-muted">
          Showing {page.contacts.length} of {page.total}
        </span>
      </div>

      <div className="flex items-center gap-2 border-b border-theme-border px-4 py-2.5">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-theme-border bg-theme-background px-3 py-1.5">
          <span className="material-icons-outlined text-[16px] text-theme-fg-muted">search</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runSearch(searchInput);
              }
            }}
            placeholder="Search name, email, or phone…"
            disabled={isSearching}
            className="w-full bg-transparent text-[12.5px] text-theme-fg outline-none disabled:opacity-60"
          />
          {page.query && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                runSearch("");
              }}
              disabled={isSearching}
              className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-theme-fg-muted hover:text-theme-fg"
              aria-label="Clear search"
            >
              <span className="material-icons-outlined text-[14px]">close</span>
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => runSearch(searchInput)}
          disabled={isSearching}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-theme-primary text-white disabled:opacity-60"
          aria-label="Search contacts"
        >
          {isSearching ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <span className="material-icons-outlined text-[16px]">search</span>
          )}
        </button>
      </div>

      <div className="flex max-h-[360px] flex-col gap-1.5 overflow-y-auto p-3">
        {page.contacts.length === 0 && (
          <p className="px-2 py-6 text-center text-[12.5px] text-theme-fg-muted">
            {page.query ? `No contacts match "${page.query}".` : "No contacts found."}
          </p>
        )}
        {page.contacts.map((c, i) => (
          <div
            key={c.ghl_contact_id}
            className="flex items-center gap-3 rounded-xl border border-theme-border px-3 py-2"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
              {initials(c.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-theme-fg">
                {c.name || "(no name)"}
                {c.company && <span className="font-medium text-theme-fg-muted">&middot; {c.company}</span>}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-3 text-[11.5px] text-theme-fg-muted">
                {c.email && (
                  <span className="inline-flex items-center gap-1">
                    <span className="material-icons-outlined text-[13px]">mail</span>
                    {c.email}
                  </span>
                )}
                {c.phone && (
                  <span className="inline-flex items-center gap-1">
                    <span className="material-icons-outlined text-[13px]">call</span>
                    {c.phone}
                  </span>
                )}
              </div>
            </div>
            {c.tags.length > 0 && (
              <span
                className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
                style={tagPillStyle}
              >
                {c.tags[0]}
                {c.tags.length > 1 ? ` +${c.tags.length - 1}` : ""}
              </span>
            )}
          </div>
        ))}
      </div>

      {page.next_search_after && (
        <div className="border-t border-theme-border px-4 py-2.5">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl py-1.5 text-[12.5px] font-bold text-theme-primary hover:bg-theme-background disabled:opacity-60"
          >
            {isLoadingMore ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-theme-border border-t-theme-primary" />
            ) : (
              <span className="material-icons-outlined text-[16px]">expand_more</span>
            )}
            Load more
          </button>
        </div>
      )}
    </div>
  );
};
